import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  List,
  Button,
  Select,
  Tag,
  Typography,
  Space,
  Modal,
  Form,
  Input,
  message,
  Row,
  Col,
  Statistic,
} from 'antd';
import { EyeOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const Orders = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const initialBookingSet = useRef(false);

  useEffect(() => {
    const bookingId = searchParams.get('booking');
    if (bookingId && !initialBookingSet.current) {
      initialBookingSet.current = true;
      const fetchAndSetOrder = async () => {
        try {
          const response = await api.get(`/provider/orders?search=${bookingId}`);
          const ordersData = response.data.data || [];
          const order = ordersData.find((o) => o.id === parseInt(bookingId));
          if (order) {
            setSelectedOrder(order);
            setIsModalVisible(true);
          }
        } catch (err) {
          console.error('Failed to fetch order:', err);
        }
      };
      fetchAndSetOrder();
    }
  }, [searchParams]);

  const { data: orders, isLoading } = useQuery(
    ['orders', searchTerm, statusFilter],
    async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      params.append('type', 'product'); // Only show product orders, not services

      const response = await api.get(`/provider/orders?${params.toString()}`);
      return response.data.data || [];
    },
    {
      enabled: !!localStorage.getItem('token') && !!localStorage.getItem('user'),
    },
  );

  const { data: stats } = useQuery(
    'order-stats',
    async () => {
      const response = await api.get('/provider/dashboard');
      return response.data;
    },
    {
      enabled: !!localStorage.getItem('token') && !!localStorage.getItem('user'),
    },
  );

  const updateOrderStatusMutation = useMutation(
    ({ id, status, notes }) => api.put(`/provider/orders/${id}/status`, { status, notes }),
    {
      onSuccess: () => {
        message.success('Order status updated successfully');
        setIsModalVisible(false);
        setSelectedOrder(null);
        form.resetFields();
        queryClient.invalidateQueries('orders');
        queryClient.invalidateQueries('order-stats');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update order status');
      },
    },
  );

  const handleStatusUpdate = (order) => {
    setSelectedOrder(order);
    form.setFieldsValue({
      status: order.order_status,
      notes: order.provider_notes || '',
    });
    setIsModalVisible(true);
  };

  const handleSubmitStatusUpdate = (values) => {
    updateOrderStatusMutation.mutate({
      id: selectedOrder.id,
      ...values,
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      confirmed: 'blue',
      processing: 'purple',
      ready_for_pickup: 'green',
      out_for_delivery: 'cyan',
      delivered: 'green',
      completed: 'success',
      cancelled: 'red',
    };
    return colors[status] || 'default';
  };

  const getRefundStatusColor = (refundStatus) => {
    const colorMap = {
      'No Refund': 'default',
      'Partial Refund Pending': 'orange',
      'Partially Refunded': 'blue',
      'Fully Refunded': 'green',
    };
    return colorMap[refundStatus] || 'default';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NP', {
      style: 'currency',
      currency: 'NPR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div>
      <Title level={2} className="page-title">
        Orders Management
      </Title>

      {/* Order Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Orders"
              value={stats?.total_orders || orders?.length || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Pending"
              value={
                stats?.pending_bookings ||
                orders?.filter((o) => o.status === 'pending')?.length ||
                0
              }
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Completed"
              value={
                stats?.completed_bookings ||
                orders?.filter((o) => o.status === 'completed')?.length ||
                0
              }
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Revenue"
              value={
                stats?.total_revenue ||
                orders?.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0)
              }
              valueStyle={{ color: '#722ed1' }}
              formatter={(value) => formatPrice(value)}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Search
              placeholder="Search orders..."
              allowClear
              enterButton={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="pending">Pending</Option>
              <Option value="confirmed">Confirmed</Option>
              <Option value="preparing">Preparing</Option>
              <Option value="ready">Ready</Option>
              <Option value="on_the_way">On the Way</Option>
              <Option value="delivered">Delivered</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Orders List */}
      <List
        loading={isLoading}
        dataSource={orders}
        renderItem={(order) => (
          <Card className="order-card" style={{ marginBottom: 16 }}>
            <div className="order-header">
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  Order #{order.id}
                </Title>
                <Text type="secondary">{new Date(order.created_at).toLocaleDateString()}</Text>
              </div>
              <Tag color={getStatusColor(order.status)}>
                {order.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
              </Tag>
            </div>

            <div className="order-body">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Text strong>Customer: </Text>
                  <Text>{order.customer?.full_name || 'Unknown'}</Text>
                  <br />
                  <Text strong>Phone: </Text>
                  <Text>{order.customer?.phone_number || 'N/A'}</Text>
                  <br />
                  <Text strong>Delivery Address: </Text>
                  <Text>
                    {order.delivery_address
                      ? `${order.delivery_address.area || ''}, ${order.delivery_address.city || ''}`.replace(
                          /^,\s*|,\s*$/g,
                          '',
                        ) || 'N/A'
                      : 'N/A'}
                  </Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text strong>Order Type: </Text>
                  <Tag>MIXED</Tag>
                  <br />
                  <Text strong>Payment Method: </Text>
                  <Text>ONLINE</Text>
                  <br />
                  <Text strong>Payment Status: </Text>
                  <Tag color="green">PAID</Tag>
                  <br />
                  <Text strong>Refund Status: </Text>
                  <div style={{ marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
                    <Tag color={getRefundStatusColor(order.refund_status_display)}>
                      {order.refund_status_display || 'No Refund'}
                    </Tag>
                  </div>
                  <br />
                  <Text strong>Providers: </Text>
                  <div style={{ marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
                    {(() => {
                      // Get unique providers from order items
                      const itemProviders = order.items
                        ?.map((item) => item.product_service?.provider)
                        .filter(Boolean);
                      const uniqueProviders = [...new Set(itemProviders)];

                      // Remove duplicates by ID
                      const finalProviders = uniqueProviders.filter(
                        (provider, index, self) =>
                          index === self.findIndex((p) => p.id === provider.id),
                      );

                      return finalProviders.map((provider) => (
                        <div key={provider.id} style={{ marginBottom: 4 }}>
                          <div style={{ fontWeight: 500 }}>
                            {provider.full_name || 'N/A'}
                            {provider.business_name &&
                              provider.business_name !== provider.full_name && (
                                <span style={{ fontSize: '12px', color: '#666', marginLeft: 8 }}>
                                  ({provider.business_name})
                                </span>
                              )}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {provider.phone_number || 'N/A'}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </Col>
              </Row>

              {order.items && order.items.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Text strong>Order Items:</Text>
                  <List
                    size="small"
                    dataSource={order.items}
                    renderItem={(item) => (
                      <List.Item>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                          }}
                        >
                          <span>{item.product_service?.name || 'Unknown Item'}</span>
                          <span>
                            {item.quantity} x ₨{item.price_at_order}
                          </span>
                        </div>
                      </List.Item>
                    )}
                  />
                </div>
              )}
            </div>

            <div className="order-footer">
              <div>
                <Text strong>Total Amount: </Text>
                <Text style={{ fontSize: '16px', color: '#1890ff' }}>
                  {formatPrice(order.total_amount)}
                </Text>
              </div>
              <Space>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    // Navigate to order details
                  }}
                >
                  View Details
                </Button>
                <Button
                  size="small"
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => handleStatusUpdate(order)}
                >
                  Update Status
                </Button>
              </Space>
            </div>
          </Card>
        )}
      />

      {/* Update Status Modal */}
      <Modal
        title={`Update Order Status - #${selectedOrder?.id}`}
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedOrder(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitStatusUpdate}>
          <Form.Item
            name="status"
            label="Order Status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select placeholder="Select new status">
              <Option value="processing">Processing</Option>
              <Option value="ready_for_pickup">Ready for Pickup</Option>
              <Option value="out_for_delivery">Out for Delivery</Option>
              <Option value="delivered">Delivered</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Provider Notes">
            <Input.TextArea rows={3} placeholder="Add any remarks about this order update" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={updateOrderStatusMutation.isLoading}
              >
                Update Status
              </Button>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setSelectedOrder(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Orders;
